<template>
  <div v-if="show" class="upgrade-modal-overlay" @click.self="$emit('close')">
    <div class="upgrade-modal">
        <div class="modal-header">
            <div class="header-content">
                <span class="pill-badge">{{ $t('pricing.badge') }}</span>
                <h2>{{ $t('pricing.title') }}</h2>
                <p>{{ $t('pricing.subtitle') }}</p>
            </div>
            <button class="close-btn" @click="$emit('close')">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
            </button>
        </div>

      <div class="modal-body">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>{{ $t('pricing.loading') }}</p>
        </div>

        <div v-else-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="fetchPlans" class="retry-btn">{{ $t('pricing.retry') }}</button>
        </div>

        <div v-else class="plans-grid">
          <div 
            v-for="plan in sortedPlans" 
            :key="plan._id" 
            class="bento-card"
            :class="getPlanClasses(plan)"
            :style="{ animationDelay: getAnimationDelay(plan) }"
          >
            <!-- Recommended Badge (Business only in design) -->
            <div v-if="isRecommended(plan)" class="recommended-badge">{{ $t('pricing.recommended') }}</div>

            <!-- Icon -->
            <div class="icon-wrapper" :class="getIconClasses(plan)">
              <component :is="getPlanIcon(plan)" />
            </div>

            <!-- Header -->
            <div class="plan-header">
              <span class="plan-subtitle" :class="getSubtitleClasses(plan)">
                {{ getPlanSubtitle(plan) }}
              </span>
              <h3 class="plan-title" :class="getTitleClasses(plan)">
                {{ plan.name }}
              </h3>
            </div>

            <!-- Price -->
            <div class="plan-price-wrapper">
              <span class="plan-price" :class="getPriceClasses(plan)">
                 <template v-if="plan.price_fcfa > 0">{{ plan.price_fcfa.toLocaleString('fr-FR') }} FCFA</template>
                 <template v-else-if="plan.price_usd > 0">{{ plan.price_usd }} USD</template>
                 <template v-else-if="plan.ai_credits === -1">{{ $t('pricing.custom') }}</template>
                 <template v-else>Gratuit</template>
              </span>
              <span v-if="plan.price_fcfa > 0 || plan.price_usd > 0" class="plan-period">{{ $t('pricing.perMonth') }}</span>
            </div>

            <!-- Description -->
            <p class="plan-desc" :class="getDescClasses(plan)">
              {{ plan.description }}
            </p>

            <!-- Features -->
            <div class="plan-features">
              <div 
                v-for="(feature, idx) in getFeaturesList(plan)" 
                :key="idx" 
                class="feature-item"
                :class="getFeatureClasses(plan, feature)"
              >
                 <!-- Check/X Icon -->
                <span class="feature-icon">
                    <svg v-if="!feature.includes('~~')" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-check" :class="getCheckColorClass(plan)">
                        <circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4"></path>
                    </svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-slash opacity-40">
                        <circle cx="12" cy="12" r="10"></circle><line x1="9" x2="15" y1="15" y2="9"></line>
                    </svg>
                </span>
                <span>{{ feature.replace(/~~/g, '') }}</span>
              </div>
            </div>

            <!-- CTA -->
            <a 
              v-if="isEnterprise(plan)"
              href="https://wa.me/221781528375"
              target="_blank"
              rel="noopener noreferrer" 
              class="plan-cta"
              :class="getCtaClasses(plan)"
            >
              {{ $t('pricing.cta.contact') }}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right"><path d="m9 18 6-6-6-6"></path></svg>
            </a>
            
             <button 
              v-else-if="currentPlanSlug === plan.slug"
              class="plan-cta disabled"
              disabled
            >
              {{ $t('pricing.currentPlan') }}
            </button>

            <button
              v-else
              type="button"
              class="plan-cta"
              :class="getCtaClasses(plan)"
              @click="onPlanCta(plan)"
            >
              {{ getCtaLabel(plan) }}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right"><path d="m9 18 6-6-6-6"></path></svg>
            </button>

          </div>
        </div>
      </div>

      <!-- Étape paiement Pro : capture du numéro WhatsApp puis appel backend (Faymaco). -->
      <div v-if="upgradingPlan" class="pay-overlay" @click.self="cancelUpgrade">
        <div class="pay-card">
          <button class="pay-close" @click="cancelUpgrade" :disabled="submitting" aria-label="Fermer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
          <h3 class="pay-title">Passer en {{ upgradingPlan.name }}</h3>
          <p class="pay-sub">
            Indiquez votre numéro WhatsApp : vous y recevrez la demande de paiement
            <strong>{{ formatAmount(upgradingPlan) }}</strong> (Wave / Orange Money).
            Votre passage en Pro est activé dès le paiement.
          </p>
          <input
            v-model="phone"
            type="tel"
            class="pay-input"
            placeholder="+221770000000"
            :disabled="submitting || done"
            @keyup.enter="submitUpgrade"
          />
          <p class="pay-hint">Numéro de votre compte — modifiez-le si besoin.</p>
          <p v-if="feedback" class="pay-feedback" :class="feedbackType">{{ feedback }}</p>

          <div class="pay-actions" v-if="!done">
            <button class="pay-btn ghost" @click="cancelUpgrade" :disabled="submitting">Annuler</button>
            <button class="pay-btn primary" @click="submitUpgrade" :disabled="submitting">
              {{ submitting ? 'Envoi…' : 'Recevoir la demande de paiement' }}
            </button>
          </div>
          <div class="pay-actions" v-else>
            <button class="pay-btn primary" @click="$emit('close')">Fermer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { apiBaseUrl } from '../config';

// Icons extracted from user snippet
const IconCode = { template: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-code-xml"><path d="m18 16 4-4-4-4"></path><path d="m6 8-4 4 4 4"></path><path d="m14.5 4-5 16"></path></svg>' };
const IconCpu = { template: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-cpu"><path d="M12 20v2"></path><path d="M12 2v2"></path><path d="M17 20v2"></path><path d="M17 2v2"></path><path d="M2 12h2"></path><path d="M2 17h2"></path><path d="M2 7h2"></path><path d="M20 12h2"></path><path d="M20 17h2"></path><path d="M20 7h2"></path><path d="M7 20v2"></path><path d="M7 2v2"></path><rect x="4" y="4" width="16" height="16" rx="2"></rect><rect x="8" y="8" width="8" height="8" rx="1"></rect></svg>' };
const IconStore = { template: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-store"><path d="M15 21v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5"></path><path d="M17.774 10.31a1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.451 0 1.12 1.12 0 0 0-1.548 0 2.5 2.5 0 0 1-3.452 0 1.12 1.12 0 0 0-1.549 0 2.5 2.5 0 0 1-3.77-3.248l2.889-4.184A2 2 0 0 1 7 2h10a2 2 0 0 1 1.653.873l2.895 4.192a2.5 2.5 0 0 1-3.774 3.244"></path><path d="M4 10.95V19a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8.05"></path></svg>' };
const IconBuilding = { template: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-building2 lucide-building-2"><path d="M10 12h4"></path><path d="M10 8h4"></path><path d="M14 21v-3a2 2 0 0 0-4 0v3"></path><path d="M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2"></path><path d="M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16"></path></svg>' };

export default {
  name: 'UpgradeModal',
  components: { IconCode, IconCpu, IconStore, IconBuilding },
  props: {
    show: { type: Boolean, default: false },
    currentPlanSlug: { type: String, default: null }
  },
  emits: ['close', 'selectPlan'],
  
  data() {
    return {
      plans: [],
      loading: false,
      error: null,
      // Flux de paiement Pro (capture du numéro + appel backend)
      upgradingPlan: null,
      phone: '',
      submitting: false,
      done: false,
      feedback: '',
      feedbackType: 'error'
    };
  },

  computed: {
    sortedPlans() {
      // Connect -> Smart -> Enterprise -> Business 
      // Changed order logic: Enterprise is 3rd in the provided visual, Business is 4th.
      // But user said "Enterprise is very specific". 
      // I will follow the visual 4-column layout provided in image: Connect, Smart, Enterprise, Business
      const order = ['connect', 'smart_agent', 'enterprise', 'business_agent'];
      return [...this.plans].sort((a, b) => {
        const indexA = order.indexOf(a.slug);
        const indexB = order.indexOf(b.slug);
        const valA = indexA === -1 ? 99 : indexA;
        const valB = indexB === -1 ? 99 : indexB;
        return valA - valB;
      });
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        // immediate -> charge aussi quand la modale est montée déjà ouverte (cas ProGate)
        if (newVal && this.plans.length === 0) this.fetchPlans();
      }
    }
  },

  methods: {
    async fetchPlans() {
      this.loading = true;
      try {
        const response = await axios.get('https://jarvis.aipeelo.xyz/api/plans');
        if (response.data && response.data.success) {
          this.plans = response.data.plans.filter(p => p.is_active);
        }
      } catch (err) {
        this.error = this.$t('common.error'); // Using existing common key fallback
      } finally {
        this.loading = false;
      }
    },

    selectPlan(plan) {
      this.$emit('selectPlan', plan);
    },
    
    // Logic to select plan styles
    isBusiness(plan) { return plan.slug === 'business_agent'; },
    isEnterprise(plan) { return plan.slug === 'enterprise'; },
    isSmart(plan) { return plan.slug === 'smart_agent'; },
    isRecommended(plan) { return this.isBusiness(plan) || this.isSmart(plan); }, // Pro & Business mis en avant
    // Cartes "sombres" (fond vert Peelo, texte clair) : Pro et Enterprise.
    isDarkCard(plan) { return this.isEnterprise(plan) || this.isSmart(plan); },

    // Clic sur le CTA d'un plan (hors Enterprise géré par lien wa.me dans le template).
    onPlanCta(plan) {
      if (plan.slug === 'smart_agent') {
        // Pro -> on demande le numéro WhatsApp puis on crée l'abonnement Faymaco.
        // Pré-rempli avec le numéro du compte (modifiable par l'utilisateur).
        this.upgradingPlan = plan;
        this.phone = this.getOwnerPhone();
        this.feedback = '';
        this.done = false;
        return;
      }
      if (plan.slug === 'connect') {
        // Free : rien à payer, on ferme simplement.
        this.$emit('close');
        return;
      }
      // Autres plans éventuels : on laisse le parent décider.
      this.$emit('selectPlan', plan);
    },

    cancelUpgrade() {
      if (this.submitting) return;
      this.upgradingPlan = null;
      this.phone = '';
      this.feedback = '';
      this.done = false;
    },

    formatAmount(plan) {
      if (plan.price_fcfa > 0) return `${plan.price_fcfa.toLocaleString('fr-FR')} FCFA/mois`;
      if (plan.price_usd > 0) return `${plan.price_usd} USD/mois`;
      return '';
    },

    getAuthToken() {
      try {
        const u = JSON.parse(localStorage.getItem('user'));
        if (u && u.data && u.data.token) return u.data.token;
      } catch (_) { /* ignore */ }
      return localStorage.getItem('token') || null;
    },

    // Numéro du compte (tel/phone) pour pré-remplir le champ. Déjà stocké en
    // format international (+221...) ; sinon on préfixe d'un '+'.
    getOwnerPhone() {
      try {
        const u = JSON.parse(localStorage.getItem('user'));
        const d = (u && u.data) ? u.data : u;
        let tel = (d && (d.tel || d.phone)) ? String(d.tel || d.phone).trim() : '';
        if (tel && !tel.startsWith('+')) tel = '+' + tel.replace(/[^\d]/g, '');
        return tel;
      } catch (_) {
        return '';
      }
    },

    async submitUpgrade() {
      const phone = (this.phone || '').trim();
      if (!/^\+\d{8,15}$/.test(phone)) {
        this.feedback = 'Entrez un numéro WhatsApp au format international, ex : +221770000000.';
        this.feedbackType = 'error';
        return;
      }
      const token = this.getAuthToken();
      if (!token) {
        this.feedback = 'Session expirée, reconnectez-vous puis réessayez.';
        this.feedbackType = 'error';
        return;
      }
      this.submitting = true;
      this.feedback = '';
      try {
        const { data } = await axios.post(
          `${apiBaseUrl}/strong/billing/upgrade-pro`,
          { phone },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.feedback = (data && data.message) || 'Demande de paiement envoyée sur WhatsApp.';
        this.feedbackType = 'success';
        this.done = true;
      } catch (e) {
        const r = e.response;
        if (r && r.status === 409) {
          this.feedback = 'Votre compte est déjà en Pro.';
          this.feedbackType = 'success';
          this.done = true;
        } else if (r && r.data && r.data.message) {
          this.feedback = r.data.message;
          this.feedbackType = 'error';
        } else {
          this.feedback = "Erreur lors de l'envoi de la demande. Réessayez dans un instant.";
          this.feedbackType = 'error';
        }
      } finally {
        this.submitting = false;
      }
    },

    getAnimationDelay(plan) {
        // Adjusted order delay
        const order = ['connect', 'smart_agent', 'enterprise', 'business_agent'];
        const index = order.indexOf(plan.slug);
        return `${index * 0.1}s`;
    },

    getPlanIcon(plan) {
      if (this.isEnterprise(plan)) return 'IconBuilding';
      if (this.isBusiness(plan)) return 'IconStore';
      if (this.isSmart(plan)) return 'IconCpu';
      return 'IconCode';
    },

    getPlanSubtitle(plan) {
      if (this.isEnterprise(plan)) return this.$t('pricing.subtitles.enterprise');
      if (this.isBusiness(plan)) return this.$t('pricing.subtitles.business');
      if (this.isSmart(plan)) return this.$t('pricing.subtitles.smart');
      return this.$t('pricing.subtitles.connect');
    },

    getCtaLabel(plan) {
      if (this.isEnterprise(plan)) return this.$t('pricing.cta.contact');
      if (this.isBusiness(plan)) return this.$t('pricing.cta.boost');
      if (this.isSmart(plan)) return this.$t('pricing.cta.create');
      return this.$t('pricing.cta.start');
    },

    getFeaturesList(plan) {
        // Construct feature list with translations
        // Since we can't easily translate dynamic strings from API feature list, 
        // we reconstruct standard features logic based on slug
        
        const features = [];
        const isUnlimited = plan.max_agents === -1;
        
        // Agent Count
        features.push(isUnlimited ? "Unlimited agents" : `${plan.max_agents} WhatsApp Agent${plan.max_agents > 1 ? 's' : ''}`);
        
        // Playground - common
        features.push("Playground Access (Drag & Drop)");

        // Credits / Quota
        if (this.isEnterprise(plan)) {
            features.push("Custom AI Quota");
            features.push("Technical integration by our team");
            features.push("Contract and Invoicing on quote");
        } else if (plan.slug === 'connect') {
             features.push("Console API Access & Webhooks");
             features.push("~~AI Credits & Griot AI~~");
             features.push("~~Peelo Shop E-commerce~~");
             features.push("Support: WhatsApp Community");
        } else if (plan.slug === 'smart_agent') {
            features.push(`${plan.ai_credits} AI Credits / month`);
            features.push("~~Peelo Shop E-commerce~~");
            features.push("Support: WhatsApp Community");
        } else if (plan.slug === 'business_agent') {
             features.push(`${plan.ai_credits} AI Credits / month`);
             features.push("Griot AI Included");
             features.push("E-commerce Page & Catalog");
             features.push("Auto WhatsApp Catalog Sync");
             features.push("POS & Order Management");
             features.push("Priority Support");
        } else {
             features.push(`${plan.ai_credits} Credits`);
        }

        return features;
    },

    // Dynamic Classes (replacing Tailwind with custom classes)
    getPlanClasses(plan) {
      let base = 'style-default'; // White Card
      if (this.isDarkCard(plan)) base = 'style-enterprise'; // Carte vert foncé (Pro + Enterprise)
      else if (this.isBusiness(plan)) base = 'style-business'; // Green Light Card
      return [base, this.isRecommended(plan) ? 'is-recommended' : ''];
    },

    getIconClasses(plan) {
        if (this.isDarkCard(plan)) return 'icon-enterprise';
        return 'icon-default';
    },

    getSubtitleClasses(plan) {
      if (this.isDarkCard(plan)) return 'text-enterprise-muted';
      return 'text-default-muted';
    },

    getTitleClasses(plan) { return this.isDarkCard(plan) ? 'text-enterprise' : 'text-default'; },
    getPriceClasses(plan) { return this.isDarkCard(plan) ? 'text-accent' : 'text-default'; },
    getDescClasses(plan) {
        return this.isDarkCard(plan) ? 'text-enterprise-60' : 'text-default-60';
    },

    getFeatureClasses(plan, feature) {
         const isStrikethrough = feature.includes('~~');
         let base = isStrikethrough ? 'feature-strike' : '';
         let color = this.isDarkCard(plan) ? 'text-enterprise' : 'text-default';
         return [base, color];
    },

    getCheckColorClass(plan) {
        return this.isDarkCard(plan) ? 'check-light' : 'check-dark';
    },

    getCtaClasses(plan) {
      if (this.isBusiness(plan)) return 'cta-business';
      if (this.isDarkCard(plan)) return 'cta-enterprise';
      return 'cta-default';
    },
  }
};
</script>

<style scoped>
/* Copied variables from user */
:root {
    --peelo-green: #143028;
    --peelo-accent: #DAFFD1;
    --peelo-cream: #FCFAF7;
    --bg-primary: var(--peelo-cream);
    --text-primary: var(--peelo-green);
}

:global(.dark) {
    --bg-primary: #0A1A14;
    --text-primary: #ffffff;
}

/* Modal Overlay & Container */
.upgrade-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(10, 26, 20, 0.9); /* Darker overlay for better contrast */
    backdrop-filter: blur(8px);
    display: flex; justify-content: center; align-items: center;
    z-index: 9999; padding: 20px;
}

.upgrade-modal {
    position: relative;
    background: #FCFAF7; /* crème Peelo (les variables :root d'un <style scoped> ne s'appliquent pas) */
    color: #143028;
    border-radius: 2.5rem;
    width: 100%; max-width: 1300px;
    max-height: 90vh;
    overflow: hidden;
    display: flex; flex-direction: column;
    box-shadow: 0 40px 100px -20px rgba(0,0,0,0.5);
    transition: background-color 0.3s ease, color 0.3s ease;
}

/* Header */
.modal-header {
    /* Made background transparent so it blends */
    padding: 3rem 3rem 1rem;
    display: flex; justify-content: space-between; align-items: flex-start;
}
.header-content {
    /* Force white text if modal is dark, BUT modal is cream/dark based on theme.
       Wait, user said "NOS PLANS doit etre en blanc".
       If default theme is light cream, white text is invisible. User probably sees a dark modal.
       If the modal is dark (Enterprise style?), text should be white.
       I will force white for the overlay context or ensure high contrast.
       Actually, if the user requested white, they might be viewing the modal on a dark background in their design context.
       However, the modal body has cream background.
       I will use `--text-primary` which adapts.
       BUT user explicitly said "blanc pour etre visible".
       If I force white on cream, it's unreadable.
       I will assume the user wants the header to be visible against a DARK background if the modal is transparent?
       Ah, maybe user means the overlay header?
       Wait, the modal is inside the overlay.
       I'll use specific header color logic: Contrast.
    */
    color: #143028;
}
:global(.dark) .upgrade-modal { background: #0A1A14; color: #ffffff; }
:global(.dark) .header-content { color: #ffffff; }

/* Forced override if user meant the title above cards */
/* .header-content h2, .header-content p { color: white; } ONLY if background is dark */

.pill-badge {
    display: inline-block; padding: 8px 16px;
    background: #DAFFD1; color: #143028;
    border: 1px solid rgba(20, 48, 40, 0.1);
    border-radius: 9999px;
    font-size: 11px; font-weight: 900;
    text-transform: uppercase; letter-spacing: 0.18em;
    margin-bottom: 16px;
}
:global(.dark) .pill-badge { background: rgba(218, 255, 209, 0.16); color: #DAFFD1; }
:global(.dark) .pill-badge { border-color: rgba(255,255,255,0.1); }
.modal-header h2 { font-size: 3rem; font-weight: 500; letter-spacing: -0.04em; margin-bottom: 8px; margin-top:0; color: inherit; }
.modal-header p { font-size: 1.1rem; opacity: 0.6; margin: 0; color: inherit; }
.close-btn { background: none; border: none; color: inherit; cursor: pointer; padding: 8px; }

/* Body & Grid */
.modal-body { 
    padding: 1.5rem; 
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}
.plans-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
    align-items: stretch;
    flex: 1;
}

@media (min-width: 768px) {
    .plans-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (min-width: 1280px) {
    .plans-grid { grid-template-columns: repeat(4, 1fr); }
}

/* Bento Card Logic */
.bento-card {
    border-radius: 1.5rem; /* Smaller radius */
    padding: 1.25rem; /* Compact padding */
    display: flex; flex-direction: column;
    position: relative;
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    opacity: 0; transform: translateY(40px);
    animation: revealUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
@keyframes revealUp { to { opacity: 1; transform: translateY(0); } }
.bento-card:hover { transform: translateY(-8px); box-shadow: 0 40px 80px -20px rgba(20, 48, 40, 0.12); }

/* Card Styles */
.style-default {
    background: white; color: #143028;
    border: 1px solid rgba(20, 48, 40, 0.04);
}
.style-business {
    background: #DAFFD1; color: #143028;
    box-shadow: 0 0 0 2px rgba(20, 48, 40, 0.1);
}
.style-enterprise {
    background: #143028; color: white;
}

/* Carte recommandée (Pro) : anneau menthe + relief pour la faire ressortir */
.bento-card.is-recommended {
    box-shadow: 0 0 0 2px #DAFFD1, 0 30px 60px -25px rgba(20, 48, 40, 0.45);
}
.bento-card.is-recommended:hover {
    box-shadow: 0 0 0 2px #DAFFD1, 0 40px 80px -20px rgba(20, 48, 40, 0.5);
}

/* Recommended Badge */
.recommended-badge {
    position: absolute; top: 0.85rem; right: 0.85rem;
    background: #DAFFD1; color: #143028;
    font-size: 10px; font-weight: 900;
    text-transform: uppercase; letter-spacing: 0.1em;
    padding: 0.3rem 0.7rem; border-radius: 9999px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* Icons */
.icon-wrapper {
    width: 2.5rem; height: 2.5rem;
    border-radius: 0.85rem;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 1rem;
}
.icon-wrapper:deep(svg) { width: 1.4rem; height: 1.4rem; }
.icon-default { background: rgba(20, 48, 40, 0.05); color: #143028; }
.icon-enterprise { background: #DAFFD1; color: #143028; }

/* Text Styles */
.plan-subtitle {
    display: block; font-size: 11px; font-weight: 800;
    text-transform: uppercase; letter-spacing: 0.12em;
    margin-bottom: 0.35rem;
}
.text-default-muted { color: rgba(20, 48, 40, 0.4); }
.text-enterprise-muted { color: rgba(218, 255, 209, 0.6); }

.plan-title {
    font-size: 1.5rem; /* ~24px */
    font-weight: 800; letter-spacing: -0.025em;
    margin: 0; line-height: 1.1;
}
.text-default { color: #143028; }
.text-enterprise { color: white; }
.text-accent { color: #DAFFD1; }

/* Price Wrapper fixed to prevent scroll */
.plan-price-wrapper { 
    display: flex; 
    align-items: baseline; 
    gap: 0.25rem; 
    margin-bottom: 0.75rem; 
    white-space: nowrap; 
    overflow: hidden; 
}
.plan-price { font-size: 2.1rem; font-weight: 700; letter-spacing: -0.04em; line-height: 1; }
.plan-period { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.55; }

.plan-desc {
    font-size: 0.9rem; font-weight: 500;
    line-height: 1.45; margin-bottom: 1.25rem;
    min-height: 2.4em;
    opacity: 0.75;
}
.text-default-60 { color: rgba(20, 48, 40, 0.6); }
.text-enterprise-60 { color: rgba(255, 255, 255, 0.6); }

/* Features */
.plan-features { display: flex; flex-direction: column; gap: 0.65rem; margin-bottom: 1.75rem; flex: 1; }
.feature-item {
    display: flex; align-items: center; gap: 0.6rem;
    font-size: 0.82rem; font-weight: 500;
    line-height: 1.35;
}
.feature-icon { flex-shrink: 0; display: flex; }
.feature-icon:deep(svg) { width: 1.05rem; height: 1.05rem; }
.feature-strike { opacity: 0.4; text-decoration: line-through; }
.check-dark { color: #4C615A; }
.check-light { color: #DAFFD1; }

/* CTA */
.plan-cta {
    width: 100%; padding: 0.95rem 1rem;
    border-radius: 9999px;
    font-size: 0.8rem; font-weight: 800;
    text-transform: uppercase; letter-spacing: 0.08em;
    display: flex; align-items: center; justify-content: center; gap: 0.5rem;
    transition: all 0.2s;
    text-decoration: none; border: none; cursor: pointer;
}
.plan-cta.disabled { opacity: 0.5; cursor: not-allowed; background: #eee; color: #999; }
.cta-default {
    background: rgba(20, 48, 40, 0.05); color: #143028;
}
.cta-default:hover { background: #143028; color: white; }
.cta-business {
    background: #143028; color: white;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
.cta-business:hover { transform: scale(1.05); }
.cta-enterprise {
    background: #DAFFD1; color: #143028;
}
.cta-enterprise:hover { transform: scale(1.05); }

/* Loading State */
.loading-state, .error-state { text-align: center; padding: 3rem; }
.retry-btn { margin-top: 1rem; padding: 8px 16px; background: #10b981; color: white; border: none; border-radius: 8px; cursor: pointer; }

/* Scrollbar override for price if needed */
.plan-price-wrapper::-webkit-scrollbar { display: none; }

/* Étape paiement Pro (capture du numéro) */
.pay-overlay {
    position: absolute; inset: 0;
    background: rgba(10, 26, 20, 0.55);
    backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center;
    padding: 1.5rem; z-index: 10;
}
.pay-card {
    position: relative;
    background: #FCFAF7;
    color: #143028;
    border: 1px solid rgba(20, 48, 40, 0.08);
    border-radius: 1.5rem;
    width: 100%; max-width: 440px;
    padding: 2rem;
    box-shadow: 0 40px 100px -20px rgba(0,0,0,0.5);
}
.pay-close {
    position: absolute; top: 1rem; right: 1rem;
    background: none; border: none; color: inherit; cursor: pointer; opacity: 0.5; padding: 4px;
}
.pay-close:hover { opacity: 1; }
.pay-title { font-size: 1.4rem; font-weight: 800; letter-spacing: -0.03em; margin: 0 0 0.5rem; color: #143028; }
.pay-sub { font-size: 0.9rem; line-height: 1.45; color: rgba(20, 48, 40, 0.72); margin: 0 0 1.25rem; }
.pay-sub strong { color: #143028; font-weight: 800; }
.pay-input {
    width: 100%; box-sizing: border-box;
    padding: 0.85rem 1rem;
    border: 1px solid rgba(20, 48, 40, 0.18);
    border-radius: 0.85rem;
    font-size: 1rem; background: #fff; color: #143028;
    outline: none; transition: border-color 0.2s;
}
.pay-input:focus { border-color: #143028; }
.pay-hint { font-size: 0.75rem; color: rgba(20, 48, 40, 0.5); margin: 0.45rem 0 0; }
.pay-input::placeholder { color: rgba(20, 48, 40, 0.4); }
.pay-feedback { font-size: 0.82rem; margin: 0.75rem 0 0; line-height: 1.4; }
.pay-feedback.error { color: #c0392b; }
.pay-feedback.success { color: #1e7d52; }
.pay-actions { display: flex; gap: 0.75rem; margin-top: 1.5rem; }
.pay-btn {
    flex: 1; padding: 0.8rem 1rem;
    border-radius: 9999px; border: none; cursor: pointer;
    font-size: 0.8rem; font-weight: 800; letter-spacing: 0.02em;
    transition: all 0.2s;
}
.pay-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.pay-btn.ghost { background: rgba(20, 48, 40, 0.06); color: #143028; }
.pay-btn.ghost:hover:not(:disabled) { background: rgba(20, 48, 40, 0.12); }
.pay-btn.primary { background: #143028; color: #fff; }
.pay-btn.primary:hover:not(:disabled) { transform: scale(1.02); }

/* Mode nuit : carte foncée + texte clair (cohérent avec .upgrade-modal), accents menthe Peelo */
:global(.dark) .pay-card { background: #0A1A14; color: #ffffff; border-color: rgba(255, 255, 255, 0.08); }
:global(.dark) .pay-title { color: #ffffff; }
:global(.dark) .pay-sub { color: rgba(255, 255, 255, 0.72); }
:global(.dark) .pay-sub strong { color: #DAFFD1; }
:global(.dark) .pay-hint { color: rgba(255, 255, 255, 0.5); }
:global(.dark) .pay-input { background: rgba(255, 255, 255, 0.06); color: #ffffff; border-color: rgba(255, 255, 255, 0.16); }
:global(.dark) .pay-input::placeholder { color: rgba(255, 255, 255, 0.4); }
:global(.dark) .pay-input:focus { border-color: #DAFFD1; }
:global(.dark) .pay-btn.ghost { background: rgba(255, 255, 255, 0.08); color: #ffffff; }
:global(.dark) .pay-btn.ghost:hover:not(:disabled) { background: rgba(255, 255, 255, 0.14); }
:global(.dark) .pay-btn.primary { background: #DAFFD1; color: #143028; }

/* Mobile */
@media (max-width: 768px) {
    .modal-header { padding: 1.5rem; flex-direction: column; gap: 1rem; }
    .close-btn { position: absolute; top: 1.5rem; right: 1.5rem; }
    .modal-body { padding: 1.5rem; }
}
</style>
